import { useState, useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { getRequest } from "../Apis";
import { campaignEndpoints } from "../utils/constants/httpConstants";
import { STRINGS as COMP_STRINGS } from "../screens/Marketing/components/strings";

/**
 * Lightweight hook that returns TrackBar props (steps, currentPhase, isPaused)
 * for any campaign sub-screen (Activity Logs, Settings, Overview).
 * Fetches campaign details once to determine type and state.
 */
const useCampaignTrackBar = () => {
  const navigate = useNavigate();
  const params = useParams();
  const ids = params?.campaignId?.split("&");
  const campaignId = ids?.[0];

  const [campaignData, setCampaignData] = useState(null);

  useEffect(() => {
    if (!campaignId) return;
    (async () => {
      try {
        const resp = await getRequest(
          navigate,
          campaignEndpoints.eachCampaignDetail + "/" + campaignId,
        );
        setCampaignData(resp?.data?.data);
      } catch (err) {
        // Silent - TrackBar just won't show
      }
    })();
  }, [campaignId]);

  const isIcpCampaign = campaignData?.isIcpCampaign === true;
  const sequenceType = campaignData?.sequence_type || "";
  const stateRaw = (campaignData?.state || "").toLowerCase().replace(/ /g, "_");
  const isPaused = stateRaw === "paused";

  const pipelineSteps = useMemo(() => {
    if (isIcpCampaign && sequenceType === "manual") {
      return [
        { id: "setup", label: COMP_STRINGS.PIPELINE_SETTING_UP },
        { id: "icp-matching", label: COMP_STRINGS.ICP_MATCHING },
        { id: "review", label: COMP_STRINGS.REVIEW_THRESHOLD },
        { id: "scheduling", label: COMP_STRINGS.PIPELINE_SCHEDULING },
        { id: "scheduled", label: COMP_STRINGS.PIPELINE_SCHEDULED },
        { id: "active", label: COMP_STRINGS.PIPELINE_ACTIVE },
        { id: "completed", label: COMP_STRINGS.PIPELINE_COMPLETED },
      ];
    }
    if (isIcpCampaign) {
      return [
        { id: "setup", label: COMP_STRINGS.PIPELINE_SETTING_UP },
        { id: "icp-matching", label: COMP_STRINGS.ICP_MATCHING },
        { id: "review", label: COMP_STRINGS.REVIEW_THRESHOLD },
        { id: "analyzing", label: COMP_STRINGS.PIPELINE_ANALYZING },
        { id: "personalizing", label: COMP_STRINGS.PERSONALIZING },
        { id: "scheduling", label: COMP_STRINGS.PIPELINE_SCHEDULING },
        { id: "scheduled", label: COMP_STRINGS.PIPELINE_SCHEDULED },
        { id: "active", label: COMP_STRINGS.PIPELINE_ACTIVE },
        { id: "completed", label: COMP_STRINGS.PIPELINE_COMPLETED },
      ];
    }
    if (sequenceType === "automated" || sequenceType === "tenantPrompt") {
      return [
        { id: "setup", label: COMP_STRINGS.PIPELINE_SETTING_UP },
        { id: "analyzing", label: COMP_STRINGS.PIPELINE_ANALYZING },
        { id: "personalizing", label: COMP_STRINGS.PIPELINE_PERSONALIZING_DATA },
        { id: "scheduling", label: COMP_STRINGS.PIPELINE_SCHEDULING },
        { id: "scheduled", label: COMP_STRINGS.PIPELINE_SCHEDULED },
        { id: "active", label: COMP_STRINGS.PIPELINE_ACTIVE },
        { id: "completed", label: COMP_STRINGS.PIPELINE_COMPLETED },
      ];
    }
    return [
      { id: "setup", label: COMP_STRINGS.PIPELINE_SETTING_UP },
      { id: "personalizing", label: COMP_STRINGS.PIPELINE_PERSONALIZING_SCHEDULING },
      { id: "scheduled", label: COMP_STRINGS.PIPELINE_SCHEDULED },
      { id: "active", label: COMP_STRINGS.PIPELINE_ACTIVE },
      { id: "completed", label: COMP_STRINGS.PIPELINE_COMPLETED },
    ];
  }, [isIcpCampaign, sequenceType]);

  // Resolve current phase from API state
  const currentPhase = useMemo(() => {
    if (!stateRaw) return "loading";
    if (stateRaw === "interrupted") return "interrupted";
    if (stateRaw === "paused" || stateRaw === "running" || stateRaw === "sending") return "active";
    if (stateRaw === "completed") return "completed";
    if (stateRaw === "scheduled") return "scheduled";
    if (stateRaw === "ready_to_run") return "scheduling";
    if (stateRaw === "personalizing" || stateRaw === "personalizing_sequence" ||
        stateRaw === "ai_processing" || stateRaw === "personalizing_batch") return "personalizing";
    if (stateRaw === "analyzing_data") return "analyzing";
    if (isIcpCampaign) {
      if (stateRaw === "setting_up") return "analyzing";
      if (stateRaw === "icp_verified") return "review";
      if (stateRaw === "icp_verifying" || stateRaw === "icp_matching") return "icp-matching";
      if (stateRaw === "entry") return "icp-matching";
    }
    if (stateRaw === "setting_up" || stateRaw === "entry") return "setup";
    return "completed";
  }, [stateRaw, isIcpCampaign]);

  return {
    pipelineSteps,
    currentPhase,
    isPaused,
    isReady: !!campaignData,
    timezone: campaignData?.timezone || "",
    scheduledDate: campaignData?.scheduled_date || null,
  };
};

export default useCampaignTrackBar;
